import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DataTable } from '../table';
import type { DataTableColumn } from '../table';
import { deviceApi } from '../api';
import { FileSyncPreview, FileSyncRow } from '../types';
import { useI18n } from '../i18n';

type Props = {
  open: boolean;
  deviceId: string;
  onClose: () => void;
  onQueued?: (message: string) => void;
};

const STATUS_COLOR: Record<FileSyncRow['status'], 'success' | 'warning' | 'info' | 'default'> = {
  same: 'success',
  different: 'warning',
  serverOnly: 'info',
  deviceOnly: 'default',
  unknown: 'default',
};

const FileSyncDialog: React.FC<Props> = ({ open, deviceId, onClose, onQueued }) => {
  const { t } = useI18n();
  const [preview, setPreview] = useState<FileSyncPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !deviceId) {
      return;
    }
    let cancelled = false;
    setBusy(true);
    setError(null);
    deviceApi.getFileSync(deviceId)
      .then((data) => {
        if (!cancelled) {
          setPreview(data);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || t('files.syncLoadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, deviceId, t]);

  const columns = useMemo<DataTableColumn<FileSyncRow>[]>(() => [
    {
      id: 'path',
      label: t('files.syncPath'),
      accessor: (row) => row.path,
    },
    {
      id: 'status',
      label: t('files.syncStatus'),
      accessor: (row) => t(`files.syncState.${row.status}`),
      filter: 'select',
      render: (row) => (
        <Chip size="small" color={STATUS_COLOR[row.status]} label={t(`files.syncState.${row.status}`)} />
      ),
    },
    {
      id: 'plan',
      label: t('files.syncPlan'),
      accessor: (row) => {
        if (row.status === 'same') return t('files.syncPlanKeep');
        if (row.status === 'deviceOnly') return t('files.syncPlanRm');
        return t('files.syncPlanPush');
      },
    },
    {
      id: 'server',
      label: t('files.syncServer'),
      accessor: (row) => row.server?.hash || (row.server ? String(row.server.size ?? '') : ''),
    },
    {
      id: 'device',
      label: t('files.syncDevice'),
      accessor: (row) => row.device?.hash || (row.device ? String(row.device.size ?? '') : ''),
    },
  ], [t]);

  const changed = preview?.changed ?? 0;
  const pushesSettings = Boolean(
    preview?.files.some((row) => row.path === '/settings.json' && row.status !== 'same' && row.status !== 'deviceOnly'),
  );

  const handleApply = async () => {
    if (pushesSettings && !window.confirm(t('files.settingsPushConfirm'))) {
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const result = await deviceApi.applyFileSync(deviceId, {});
      setPreview(result.preview);
      const n = result.tasks.length;
      const skipped = result.skipped.length;
      const parts = [t('files.syncQueued', { count: n })];
      if (skipped) {
        parts.push(t('files.syncSkipped', { count: skipped }));
      }
      onQueued?.(parts.join(' · '));
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('files.syncApplyFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('files.syncTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('files.syncHint')}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {preview && !preview.reported && (
            <Alert severity="info">{t('files.syncNoInventory')}</Alert>
          )}
          {busy && !preview ? (
            <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
          ) : preview ? (
            <DataTable
              rows={preview.files}
              columns={columns}
              getRowId={(row) => row.path}
              toolbar={false}
              emptyMessage={t('files.syncEmpty')}
              maxHeight={360}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleApply} disabled={busy || !preview || changed === 0}>
          {t('files.syncApply', { count: changed })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileSyncDialog;
